// What every open-ended block is made of: the state that says how the answers are being looked
// at, and the prop bags each surface draws from. block.coffee owns the mount, the report-param
// write and the payload; everything between the two is here.
//
// A family drives this with its own lib and its own builders, and hands its extra inputs in
// through `extra` -- combo's drill-down level, the menu-test collapse -- so the memoized bag
// still turns over only when one of them moves.

import { useCallback, useMemo, useRef } from 'react'
import { getModelValue } from '@aytm/app/stats/lib/stat-page'

import { OE_PROPS_BUILDERS, calcComponentProps } from '@aytm/app/stats/page/question-views/oe/lib/props'
import { buildOnCommentsClick, calcSortOrder } from '@aytm/app/stats/page/question-views/oe/lib/table-utils'
import { buildOnWcExport } from '@aytm/app/stats/page/question-views/oe/lib/wc-export'
import useOeState from '@aytm/app/stats/page/question-views/oe/states/useOeState'
import useWcConfig from '@aytm/app/stats/page/question-views/oe/states/useWcConfig'

// A config key the researcher cleared leaves the config rather than sitting in it empty.
const nextConfig = (config, key, value) => {
  const next = structuredClone(config || {})

  if (null == value) delete next[key]
  else next[key] = value

  return next
}

export default function useOeBlock(
  {
    params,
    paramsRevision,
    lib,
    chartViewModes = [],
    chartModeViewModes,
    defaultSortOrder,
    healViewMode,
    rType,
    isTraitType,
    labelTarget,
    commentsTarget,
    onReady,
    onStateChange,
    handlers,
    id,
    parentQuestionId,
    questionKey,
    type,
    variant,
    format,
    verbatimFormat,
    components,
    answer,
    answerVariants,
    dimensions,
    disabled,
    clickable,
    questionChartMode,
    reportPercentDecimals,
    modelDefaults,
    rejects = {},
    onWcParamsChange,
    onOutdatedPreset,
    getExportExtra,
    extra
  },
  builders = OE_PROPS_BUILDERS
) {
  // The cloud rides in the same param bag but is keyed by question id, not by the block key, and
  // it seeds a state of its own -- so it is split off before the view-mode state sees the bag.
  const { wc: wcSection, ...viewParams } = params || {}

  // the cloud config is held below, but the api is handed up on mount, so the readers are bound first
  const wcConfigApi = useRef({})
  const getWcConfig = useCallback(() => wcConfigApi.current.getWcConfig?.(), [])
  const getWcPreset = useCallback(expType => wcConfigApi.current.getWcPreset?.(expType) || {}, [])
  const importWcPreset = useCallback(preset => wcConfigApi.current.importWcPreset?.(preset) || {}, [])
  const applyWcPreset = useCallback((preset, update) => wcConfigApi.current.applyWcPreset?.(preset, update), [])
  const getWcParamsSection = useCallback(config => wcConfigApi.current.getWcParamsSection?.(config) || {}, [])

  // the mounted chart knows the axis it is showing; the exporters ask the state for it
  const chartApiRef = useRef(null)

  const { viewMode, config, chartMode, zoomLevel, sortOrder, onModelChange } = useOeState({
    params: viewParams,
    paramsRevision,
    questionKey,
    type,
    variant,
    format,
    lib,
    chartViewModes,
    chartModeViewModes,
    questionChartMode,
    defaultSortOrder,
    healViewMode,
    answer,
    answerVariants,
    rType,
    isTraitType,
    labelTarget,
    chartApiRef,
    getWcConfig,
    getWcPreset,
    importWcPreset,
    applyWcPreset,
    getWcParamsSection,
    getExportExtra,
    onReady,
    onStateChange
  })

  // a config change names one key, so the change is applied against the config as it stands
  const configRef = useRef(config)
  configRef.current = config

  const onConfigChange = useCallback(
    (key, value) => onModelChange({ config: nextConfig(configRef.current, key, value) }),
    [onModelChange]
  )

  // the comments panel is opened from here, not by the shell: the identity it addresses the
  // question by is fixed, and the tag rows it filters on arrive with the click
  const onCommentsClick = useMemo(() => buildOnCommentsClick(commentsTarget || {}), [commentsTarget])

  const allHandlers = useMemo(
    () => ({
      ...handlers,
      onCommentsClick,
      // the chart hands its api to both sides: the shell serializes the svg, the state reads the axis
      onChartReady: api => {
        chartApiRef.current = api
        handlers?.onChartReady?.(api)
      },
      onViewModeChange: value => onModelChange({ viewMode: value }),
      onConfigChange,
      onChartModeChange: mode => onModelChange({ chartMode: mode }),
      // the chart names the question it is zooming; the block has only its own to move
      onZoomChange: (key, level) => onModelChange({ zoomLevel: level }),
      onSort: (colIndex, dir, cols) => {
        const order = calcSortOrder(cols, colIndex, dir)
        if (order) onModelChange({ sortOrder: order })
      }
    }),
    [handlers, onCommentsClick, onConfigChange, onModelChange]
  )

  // Resolved on every render: the prop is the tracked input where a shell passes it, and the
  // page value is read directly where one does not -- either way the memo below sees it change.
  const percentDecimals = reportPercentDecimals ?? getModelValue('percentDecimals')

  const question = {
    id,
    questionId: id,
    parentQuestionId,
    questionKey,
    type,
    variant,
    format,
    verbatimFormat,
    components,
    answer,
    answerVariants,
    dimensions,
    disabled,
    clickable,
    questionChartMode,
    reportPercentDecimals: percentDecimals,
    lib,
    chartViewModes,
    viewMode,
    config,
    chartMode,
    zoomLevel,
    sortOrder,
    ...extra
  }

  // The corpus the family draws its cloud from; a family with nested answers derives it from the
  // level it is drilled into, so it is the family's own builder that answers.
  const rawData = builders.calcRawData(question)
  const corpusSize = rawData?.words?.length || 0

  const {
    config: cloudConfig,
    handlers: cloudHandlers,
    api: cloudApi
  } = useWcConfig({
    id,
    questionKey,
    modelDefaults,
    rejects,
    corpusSize,
    rawData: Boolean(format?.useRawData),
    storedSection: wcSection,
    paramsRevision,
    onOutdatedPreset,
    onParamsChange: onWcParamsChange
  })

  wcConfigApi.current = cloudApi

  // the word list a CSV is built from is the cloud's own, so only an image export needs the shell
  const onWcExport = useMemo(
    () => buildOnWcExport({ questionKey, mergeWords: cloudConfig?.mergeWords, onExport: allHandlers.onWcExport }),
    [questionKey, cloudConfig, allHandlers]
  )

  // the strip reports the click; the bag every other cloud is poured from is this one's own
  const onApplyToAll = useMemo(
    () => () => allHandlers.onApplyToAll?.(cloudApi.getWcPreset?.('full')),
    [allHandlers, cloudApi]
  )

  // The cloud is the one surface with a config, an exporter and a set of edits of its own: its
  // config is the preset-derived one, not the active view mode's.
  const cloudBuilders = useMemo(
    () => ({
      ...builders,
      calcWcProps: inputs =>
        builders.calcWcProps({
          ...inputs,
          config: cloudConfig,
          // the cloud's own edits are defaults a family can beat: combo's word click drills
          // into the level instead of entering active-word mode
          handlers: { ...cloudHandlers, ...allHandlers, onExport: onWcExport, onApplyToAll }
        })
    }),
    [builders, cloudConfig, allHandlers, cloudHandlers, onWcExport, onApplyToAll]
  )

  // The page-wide settings the bags read while they are built. The shell repaints on a change to any
  // of them, but the bags are memoised, so they are read here on every render and named below; left
  // out, a block keeps the bags it built under the old setting -- a chart that opened under "no
  // charts" never draws after the switch back.
  const pageSettings = [
    'chartMode',
    'zoomLevel',
    'chartLegendMode',
    'confidenceLevel',
    'significanceLevel',
    'colorTheme'
  ]
    .map(key => getModelValue(key))
    .join('|')

  // the view list reads the tag stats while the bags are built, and the v2 loader writes them after the block
  // has drawn -- named below so the list is rebuilt when they arrive. The page writes a new object per change.
  const tagsData = getModelValue('tagsData')

  return useMemo(
    () => calcComponentProps({ ...question, rawData, handlers: allHandlers }, cloudBuilders),
    [
      id,
      lib,
      parentQuestionId,
      questionKey,
      type,
      variant,
      format,
      verbatimFormat,
      components,
      answer,
      answerVariants,
      dimensions,
      disabled,
      clickable,
      questionChartMode,
      chartViewModes,
      percentDecimals,
      pageSettings,
      tagsData,
      viewMode,
      config,
      chartMode,
      zoomLevel,
      sortOrder,
      allHandlers,
      cloudBuilders,
      extra
    ]
  )
}
